// team-bu-members.js — per-Workspace member CRUD (Phase 6.4).
//
// Renders an expandable member section under each workspace row in
// the Team → Workspaces panel. Wraps four backend endpoints:
//
//   GET    /v1/teams/bus/:buId/members         list members
//   POST   /v1/teams/bus/:buId/members         add member
//   PATCH  /v1/teams/bus/:buId/members/:userId change role_id
//   DELETE /v1/teams/bus/:buId/members/:userId remove
//
// Auth model: the server requires `admin:members` on the caller for
// every mutation (PATCH/POST/DELETE). The dashboard mirrors this by
// gating the buttons behind `canManage` (isAdmin || isOwner), same
// gate as the workspace CRUD itself. Server is authoritative.
//
// State model:
//   - `expanded`: Set<bu_id> — which workspace rows have their
//     member section open. Persisted only in-memory; collapses on
//     page refresh.
//   - `membersByBuId`: Map<bu_id, { members[], fetchedAt }> — 5s
//     cache, parallel to the workspace-list cache shape.
//   - `tenantRoles`: cached /v1/teams/roles result for the
//     role-picker dropdown. Refreshed every 5 min (roles change
//     much less often than workspace memberships).
//   - `tenantPeople`: cached /v1/teams/members for the add-member
//     picker. Same 5min TTL.

import { $ } from "./dom.js";
import { escapeHTML, ago } from "./util.js";
import { apiWithMessage } from "./api.js";
import { showConfirm, showToast } from "./ui.js";
import { teamState } from "./page-state.js";

// freshCanManage re-reads teamState.info at call time so a user
// demoted mid-session doesn't see admin buttons render on the
// next mutation-driven repaint. Cross-PR-sweep finding #2 — every
// repaint-after-mutation path used to hardcode `true` because the
// caller "must have been canManage to have clicked the action."
// That logic holds at click time but not after the awaited
// network call resolves: the user's role could have changed in
// between.
function freshCanManage() {
  const info = teamState.info;
  if (!info) return false;
  // Owner role retired in migration 035 — your_team_role === "owner" is
  // retained for back-compat with JWTs minted pre-migration.
  return info.your_role === "admin"
    || info.your_team_role === "admin"
    || info.your_team_role === "owner";
}

const MEMBER_FETCH_TTL_MS = 5_000;
// Lower than the 5-min instinct: a user who just created a custom
// role on the Roles page and tabs back to add someone to a
// workspace with it should see the new role within ~1 minute. The
// picker fetch hits two endpoints (/v1/teams/members,
// /v1/teams/roles) only when the add form is opened — not on
// every render — so the cost of a 60s TTL is one extra network
// call per minute that the user has the add form open.
const PICKER_FETCH_TTL_MS = 60_000;

const expanded = new Set();
const membersByBuId = new Map();

let tenantRoles = null;
let tenantRolesFetchedAt = 0;
let tenantPeople = null;
let tenantPeopleFetchedAt = 0;

// isExpanded — exported so team-bus.js can include the expanded
// section markup inline when re-rendering the workspace list.
export function isBuExpanded(buId) {
  return expanded.has(buId);
}

// toggleBuExpanded flips the open/closed state for one workspace
// row. Called from the workspace row's Members toggle button.
// Triggers a re-render (caller responsibility) so the new state
// shows.
export function toggleBuExpanded(buId) {
  if (expanded.has(buId)) expanded.delete(buId);
  else expanded.add(buId);
}

// Reset on workspace mutation (delete) so a now-defunct bu_id
// doesn't sit in the expanded set forever.
export function dropBuFromExpanded(buId) {
  expanded.delete(buId);
  membersByBuId.delete(buId);
}

// invalidateBuMembersCache forces the next render to refetch.
// Called after every successful CRUD on this BU's members.
function invalidateBuMembersCache(buId) {
  membersByBuId.delete(buId);
}

// invalidateTenantRolesCache is called from team-roles.js after a
// role mutation (create / edit / delete). Without this, the
// role-picker dropdowns in the workspace add-member form + the
// change-role modal stay stale for up to PICKER_FETCH_TTL_MS
// (60s) — a user who creates a "Compliance Reviewer" role on the
// Roles page and immediately wants to assign it on Settings sees
// no such option in the dropdown.
//
// Cleared along with the fetched-at timestamp so the next call
// re-fetches rather than returning the null cache as "fresh."
export function invalidateTenantRolesCache() {
  tenantRoles = null;
  tenantRolesFetchedAt = 0;
}

// invalidateTenantPeopleCache mirrors the roles invalidator for
// when /v1/teams/members mutates (member added / removed). Same
// 60s TTL otherwise. Exported in case future PRs add a roster-
// mutation flow that wants to refresh the picker immediately.
export function invalidateTenantPeopleCache() {
  tenantPeople = null;
  tenantPeopleFetchedAt = 0;
}

// invalidateAllBuMembersCaches clears every per-Workspace member
// cache. Used by the bu:changed listener: when the user flips the
// sidebar workspace switcher, the active context changed enough
// that next-paint should refetch all members rather than serve
// from a cache scoped to the prior context.
export function invalidateAllBuMembersCaches() {
  membersByBuId.clear();
}

// bu:changed listener — sidebar workspace switcher fires this on
// every flip. Cross-PR-sweep finding #4: without this hook a user
// who expanded "Engineering" → looked at members → switched the
// sidebar to "Marketing" → came back to Settings would see the
// stale Engineering member list because the 5s cache still held
// it. The switch may have happened *implicitly* in another tab
// (the bu:changed event also fires from bu-switcher.js's own
// selectBu callback). Clearing on every flip is cheap (the cache
// is hot only for currently-expanded rows) and correct.
//
// Module-load attach: team-bu-members.js is imported by
// team-bus.js which is imported by pages/settings.js. The chain
// resolves at app boot via main.js so by the time bu-switcher.js
// can fire bu:changed (after teamState.info loads), this listener
// is wired.
if (typeof document !== "undefined") {
  document.addEventListener("bu:changed", () => {
    invalidateAllBuMembersCaches();
  });
}

// fetchBuMembers — cached fetch of one workspace's member list.
// Returns the cached list immediately if fresh; otherwise fetches
// then renders.
async function fetchBuMembers(buId) {
  const cached = membersByBuId.get(buId);
  if (cached && Date.now() - cached.fetchedAt < MEMBER_FETCH_TTL_MS) {
    return cached.members;
  }
  const resp = await apiWithMessage(`/v1/teams/bus/${encodeURIComponent(buId)}/members`);
  const members = Array.isArray(resp?.members) ? resp.members : [];
  membersByBuId.set(buId, { members, fetchedAt: Date.now() });
  return members;
}

async function fetchTenantRoles() {
  if (tenantRoles && Date.now() - tenantRolesFetchedAt < PICKER_FETCH_TTL_MS) {
    return tenantRoles;
  }
  const resp = await apiWithMessage("/v1/teams/roles");
  tenantRoles = Array.isArray(resp?.roles) ? resp.roles : [];
  tenantRolesFetchedAt = Date.now();
  return tenantRoles;
}

async function fetchTenantPeople() {
  if (tenantPeople && Date.now() - tenantPeopleFetchedAt < PICKER_FETCH_TTL_MS) {
    return tenantPeople;
  }
  // /v1/teams/members returns both a flat laptop list (`members`,
  // keyed by laptop_id) AND an identity-grouped view (`people`, each
  // with user_id). The per-Workspace POST expects that user_id, so
  // read from `people`.
  const resp = await apiWithMessage("/v1/teams/members");
  const people = Array.isArray(resp?.people) ? resp.people : [];
  tenantPeople = people.map((p) => ({
    user_id: p.user_id,
    email: p.email || "",
  }));
  tenantPeopleFetchedAt = Date.now();
  return tenantPeople;
}

// renderBuMembersSection — the markup inserted directly under an
// expanded workspace row. Renders a loading skeleton synchronously,
// then async-fills the body once the fetch resolves. The container
// is identified by data-bu-members-for=<bu_id> so a re-fetch can
// re-target the same DOM slot without rebuilding the whole panel.
export function renderBuMembersSection(buId, canManage) {
  // Skeleton — fills in once fetchBuMembers resolves.
  setTimeout(() => paintBuMembersSection(buId, canManage), 0);
  return `
    <div class="bu-members-section" data-bu-members-for="${escapeHTML(buId)}"
      style="border-bottom: 1px solid var(--border-subtle, var(--border)); background: var(--bg-elevated); padding: var(--s4) var(--s5);">
      <div style="font-size: 11px; color: var(--text-tertiary); text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: var(--s3);">
        Members
      </div>
      <div class="bu-members-body" data-bu-members-body="${escapeHTML(buId)}"
        style="font-size: 12px; color: var(--text-tertiary);">
        Loading...
      </div>
    </div>`;
}

async function paintBuMembersSection(buId, canManage) {
  const body = document.querySelector(
    `[data-bu-members-body="${cssAttrEscape(buId)}"]`,
  );
  if (!body) return; // user collapsed before fetch landed

  let members;
  try {
    members = await fetchBuMembers(buId);
  } catch (err) {
    const msg = (err && err.message) || "Failed to load members";
    body.innerHTML =
      `<div style="color: var(--danger); font-size: 12px;">${escapeHTML(msg)}</div>`;
    return;
  }

  if (members.length === 0) {
    const adminHint = canManage
      ? `Use <strong style="color: var(--text-secondary);">+ Add member</strong> below to invite someone.`
      : "";
    body.innerHTML =
      `<div style="font-size: 12px; color: var(--text-tertiary); font-style: italic; margin-bottom: var(--s3);">
         No members yet. ${adminHint}
       </div>`;
  } else {
    body.innerHTML = members
      .map((m) => renderMemberRow(buId, m, canManage))
      .join("");
  }

  // Add-member form (admins only)
  if (canManage) {
    body.insertAdjacentHTML("beforeend", renderAddMemberStub(buId));
    wireAddMember(buId, body);
  }

  // Per-row action wiring
  if (canManage) wireMemberRowActions(buId, body);
}

// CSS attribute selectors don't accept arbitrary characters. The
// bu_id format is "bu_<hex16>" — alphanumerics only — so escaping
// is mostly defensive against unexpected shapes from a future
// server-side change. Wraps the value so a stray quote or
// backslash doesn't break the selector.
function cssAttrEscape(s) {
  return String(s).replace(/(["\\])/g, "\\$1");
}

function renderMemberRow(buId, m, canManage) {
  const email = m.email || `<unknown user ${escapeHTML((m.user_id || "").slice(0, 8))}>`;
  const roleName = m.role_name || m.role_id || "(no role)";
  const actions = canManage
    ? `<div style="display: flex; gap: var(--s2); align-items: center;">
         <button class="btn btn-sm bu-member-change-role"
           data-bu-id="${escapeHTML(buId)}"
           data-user-id="${escapeHTML(m.user_id)}"
           data-current-role-id="${escapeHTML(m.role_id)}"
           data-email="${escapeHTML(m.email || "")}"
           style="background: none; border: 1px solid var(--border); color: var(--text-secondary); font-size: 11px; padding: 3px 8px;">Change role</button>
         <button class="btn btn-sm bu-member-remove"
           data-bu-id="${escapeHTML(buId)}"
           data-user-id="${escapeHTML(m.user_id)}"
           data-email="${escapeHTML(m.email || "")}"
           style="background: none; border: 1px solid var(--danger); color: var(--danger); font-size: 11px; padding: 3px 8px;">Remove</button>
       </div>`
    : "";
  return `
    <div class="bu-member-row" data-user-id="${escapeHTML(m.user_id)}"
      style="display: flex; align-items: center; justify-content: space-between; gap: var(--s3); padding: var(--s2) 0;">
      <div style="display: flex; align-items: center; gap: var(--s3); min-width: 0; flex: 1;">
        <span style="font-size: 12.5px; color: var(--text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHTML(email)}</span>
        <span style="font-size: 10px; padding: 1px 8px; border-radius: 999px; background: var(--bg-panel); color: var(--text-secondary); font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em;">${escapeHTML(roleName)}</span>
        <span style="font-size: 11px; color: var(--text-tertiary);">added ${escapeHTML(ago(m.added_at))}</span>
      </div>
      ${actions}
    </div>`;
}

function renderAddMemberStub(buId) {
  return `
    <div class="bu-member-add" data-bu-id="${escapeHTML(buId)}"
      style="margin-top: var(--s3); padding-top: var(--s3); border-top: 1px solid var(--border);">
      <button class="btn btn-sm bu-member-add-show"
        data-bu-id="${escapeHTML(buId)}"
        style="background: none; border: 1px solid var(--border); color: var(--accent); font-size: 12px; padding: 4px 12px;">+ Add member</button>
      <div class="bu-member-add-form" data-bu-id="${escapeHTML(buId)}"
        style="display: none; margin-top: var(--s2); padding: var(--s3); background: var(--bg-panel); border: 1px solid var(--border); border-radius: var(--r-sm);">
        <div style="display: flex; gap: var(--s2); align-items: center; flex-wrap: wrap;">
          <select class="bu-member-add-user" style="flex: 1; min-width: 180px; padding: 6px 8px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-sm); color: var(--text); font-size: 12px;">
            <option value="">Loading users…</option>
          </select>
          <select class="bu-member-add-role" style="min-width: 120px; padding: 6px 8px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-sm); color: var(--text); font-size: 12px;">
            <option value="">Loading roles…</option>
          </select>
          <button class="btn btn-sm bu-member-add-submit" style="background: var(--accent); color: white; border: none; font-size: 12px;">Add</button>
          <button class="btn btn-sm bu-member-add-cancel" style="font-size: 12px;">Cancel</button>
        </div>
        <div class="bu-member-add-error" style="display: none; margin-top: 6px; font-size: 12px; color: var(--danger);"></div>
      </div>
    </div>`;
}

async function populateAddForm(buId, body) {
  const userSelect = body.querySelector(
    `.bu-member-add-form[data-bu-id="${cssAttrEscape(buId)}"] .bu-member-add-user`,
  );
  const roleSelect = body.querySelector(
    `.bu-member-add-form[data-bu-id="${cssAttrEscape(buId)}"] .bu-member-add-role`,
  );
  if (!userSelect || !roleSelect) return;

  // Existing members of THIS workspace are excluded from the picker
  // — adding them again 409s. Read from the same membersByBuId cache
  // (already filled by the section paint).
  const currentMembers = (membersByBuId.get(buId) || {}).members || [];
  const excludedIds = new Set(currentMembers.map((m) => m.user_id));

  let people = [];
  let roles = [];
  try {
    [people, roles] = await Promise.all([
      fetchTenantPeople(),
      fetchTenantRoles(),
    ]);
  } catch (err) {
    const msg = (err && err.message) || "Failed to load roster";
    showToast("error", msg);
    return;
  }

  // People dropdown: tenant roster minus current workspace members.
  // Each option's value is the user_id; the label is the email
  // (falling back to a truncated id if email is missing — pre-link
  // members may have null emails).
  const eligible = people.filter((p) => !excludedIds.has(p.user_id));
  if (eligible.length === 0) {
    userSelect.innerHTML = `<option value="">All tenant members already in this workspace</option>`;
    userSelect.disabled = true;
  } else {
    userSelect.disabled = false;
    userSelect.innerHTML =
      `<option value="">Select user…</option>` +
      eligible
        .map((p) => {
          const label = p.email || `unlinked (${(p.user_id || "").slice(0, 8)})`;
          return `<option value="${escapeHTML(p.user_id)}">${escapeHTML(label)}</option>`;
        })
        .join("");
  }

  // Roles dropdown: tenant's role catalog. Pre-select "Member" if
  // present (most common case for a new workspace member).
  const memberRole = roles.find((r) => r.name === "Member");
  roleSelect.innerHTML = roles
    .map((r) => {
      const selected = memberRole && r.role_id === memberRole.role_id ? " selected" : "";
      return `<option value="${escapeHTML(r.role_id)}"${selected}>${escapeHTML(r.name)}</option>`;
    })
    .join("");
}

function wireAddMember(buId, body) {
  const showBtn = body.querySelector(
    `.bu-member-add-show[data-bu-id="${cssAttrEscape(buId)}"]`,
  );
  const form = body.querySelector(
    `.bu-member-add-form[data-bu-id="${cssAttrEscape(buId)}"]`,
  );
  if (!showBtn || !form) return;
  const cancelBtn = form.querySelector(".bu-member-add-cancel");
  const submitBtn = form.querySelector(".bu-member-add-submit");
  const userSelect = form.querySelector(".bu-member-add-user");
  const roleSelect = form.querySelector(".bu-member-add-role");
  const errEl = form.querySelector(".bu-member-add-error");

  showBtn.addEventListener("click", () => {
    showBtn.style.display = "none";
    form.style.display = "";
    if (errEl) errEl.style.display = "none";
    populateAddForm(buId, body);
  });
  if (cancelBtn) {
    cancelBtn.addEventListener("click", () => {
      form.style.display = "none";
      showBtn.style.display = "";
    });
  }
  if (submitBtn) {
    submitBtn.addEventListener("click", async () => {
      const userId = userSelect.value;
      const roleId = roleSelect.value;
      if (!userId) {
        if (errEl) {
          errEl.textContent = "Select a user to add";
          errEl.style.display = "";
        }
        return;
      }
      if (!roleId) {
        if (errEl) {
          errEl.textContent = "Select a role";
          errEl.style.display = "";
        }
        return;
      }
      submitBtn.disabled = true;
      try {
        await apiWithMessage(`/v1/teams/bus/${encodeURIComponent(buId)}/members`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, role_id: roleId }),
        });
        showToast("success", "Member added");
        invalidateBuMembersCache(buId);
        // Re-render this BU's members section in place by repainting.
        await paintBuMembersSection(buId, freshCanManage());
      } catch (err) {
        const msg = (err && err.message) || "Failed to add member";
        if (errEl) {
          errEl.textContent = msg;
          errEl.style.display = "";
        }
      } finally {
        submitBtn.disabled = false;
      }
    });
  }
}

function wireMemberRowActions(buId, body) {
  body.querySelectorAll(".bu-member-change-role").forEach((btn) => {
    btn.addEventListener("click", async (ev) => {
      const t = ev.currentTarget;
      const userId = t.getAttribute("data-user-id") || "";
      const currentRoleId = t.getAttribute("data-current-role-id") || "";
      const email = t.getAttribute("data-email") || "";
      await openChangeRoleModal(buId, userId, currentRoleId, email);
    });
  });
  body.querySelectorAll(".bu-member-remove").forEach((btn) => {
    btn.addEventListener("click", async (ev) => {
      const t = ev.currentTarget;
      const userId = t.getAttribute("data-user-id") || "";
      const email = t.getAttribute("data-email") || "";
      const ok = await showConfirm(
        `Remove ${email || "user"} from this workspace?`,
        `Their tenant-level role isn't affected; they'll just lose any workspace-scoped grants this membership conferred.`,
        "Remove",
        { destructive: true },
      );
      if (!ok) return;
      try {
        await apiWithMessage(
          `/v1/teams/bus/${encodeURIComponent(buId)}/members/${encodeURIComponent(userId)}`,
          { method: "DELETE" },
        );
        showToast("success", "Removed from workspace");
        invalidateBuMembersCache(buId);
        await paintBuMembersSection(buId, freshCanManage());
      } catch (err) {
        const msg = (err && err.message) || "Failed to remove member";
        showToast("error", msg);
      }
    });
  });
}

// Reuse the showPrompt-style modal pattern: a thin role-picker.
// Doing this inline as a confirm-style dialog rather than reusing
// the full Role editor modal (which is for role definitions) keeps
// the surfaces distinct — this picker just chooses an existing
// role for one user.
async function openChangeRoleModal(buId, userId, currentRoleId, email) {
  let roles = [];
  try {
    roles = await fetchTenantRoles();
  } catch (err) {
    showToast("error", (err && err.message) || "Failed to load roles");
    return;
  }
  if (roles.length === 0) {
    showToast("error", "No roles available");
    return;
  }
  // Build an HTML body for showConfirm — a <select> + helper text.
  // showConfirm renders HTML in its body.
  const options = roles
    .map((r) => {
      const selected = r.role_id === currentRoleId ? " selected" : "";
      return `<option value="${escapeHTML(r.role_id)}"${selected}>${escapeHTML(r.name)}</option>`;
    })
    .join("");
  const html =
    `<div style="font-size: 12.5px; color: var(--text-secondary); margin-bottom: var(--s3);">
       Change ${escapeHTML(email || "this user")}'s role in this workspace.
     </div>
     <select id="bu-member-change-role-select"
       style="width: 100%; padding: 8px 10px; background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-sm); color: var(--text); font-size: 13px;">${options}</select>`;

  const ok = await showConfirm(
    "Change workspace role",
    html,
    "Save",
  );
  if (!ok) return;
  const select = document.getElementById("bu-member-change-role-select");
  const nextRoleId = select && select.value;
  if (!nextRoleId || nextRoleId === currentRoleId) return;
  try {
    await apiWithMessage(
      `/v1/teams/bus/${encodeURIComponent(buId)}/members/${encodeURIComponent(userId)}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role_id: nextRoleId }),
      },
    );
    showToast("success", "Role updated");
    invalidateBuMembersCache(buId);
    await paintBuMembersSection(buId, freshCanManage());
  } catch (err) {
    showToast("error", (err && err.message) || "Failed to update role");
  }
}
